"use client"

import { isActiveApplicationStatus, applicantsHaveMatchScores } from '@yeshuman/sdk'
import type { UnifiedCandidate } from '@yeshuman/sdk'
import { useMemo } from 'react'

import type { CandidateMatch } from './use-briefs'
import {
  boardHasUnscoredActiveApplicants,
  useOpportunityApplicants,
  type ApplicantCard,
} from './use-opportunity-applicants'
import { useJobTalentSummary } from './use-job-talent-summary'

function applicantsToUnifiedCandidates(applicants: ApplicantCard[]): UnifiedCandidate[] {
  const scored = applicants
    .filter((a) => typeof a.match_score === 'number')
    .slice()
    .sort((a, b) => (b.match_score ?? 0) - (a.match_score ?? 0))

  return scored.map((applicant, index) => {
    const score = applicant.match_score ?? 0
    const rank = index + 1
    const full: CandidateMatch = {
      rank,
      profile_id: applicant.profile.id,
      candidate_name: applicant.profile.name,
      final_score: score,
      structured_score: score,
      semantic_score: score,
      evaluation_id: applicant.evaluation_id ?? null,
      found_by: ['application'],
      was_llm_judged: false,
    }
    return {
      profile_id: applicant.profile.id,
      name: applicant.profile.name,
      score,
      strategy: 'application',
      strategies: ['application'],
      state: 'analyzed' as const,
      rank,
      full,
    }
  })
}

export function useEmployerJobTalent(opportunityId: string) {
  const { board, isLoading: boardLoading } = useOpportunityApplicants(opportunityId)
  const activeApplicants = useMemo(
    () =>
      (board?.applicants ?? []).filter((applicant) =>
        isActiveApplicationStatus(applicant.status),
      ),
    [board?.applicants],
  )
  const talentQuery = useJobTalentSummary(opportunityId, true, {
    refetchWhileUnscored: boardHasUnscoredActiveApplicants(activeApplicants),
  })
  const emptyActiveApplicants = activeApplicants.length === 0
  const hasPipelineAnalysedScores = applicantsHaveMatchScores(activeApplicants)
  const rankedCandidates = useMemo(
    () => applicantsToUnifiedCandidates(activeApplicants),
    [activeApplicants],
  )

  const cohortSummary = talentQuery.data?.cohort_summary ?? ''
  const cohortSummaryAt = talentQuery.data?.cohort_summary_at ?? null
  const cohortFingerprint = talentQuery.data?.cohort_membership_fingerprint ?? ''

  return {
    board,
    activeApplicants,
    emptyActiveApplicants,
    rankedCandidates,
    hasPipelineAnalysedScores,
    cohortSummary,
    cohortSummaryAt,
    cohortFingerprint,
    isLoading: boardLoading || talentQuery.isLoading,
    isError: talentQuery.isError,
    errorMessage: talentQuery.error instanceof Error ? talentQuery.error.message : null,
    refetchTalent: talentQuery.refetch,
  }
}

/** @deprecated Use useEmployerJobTalent — kept as alias during rename. */
export const useEmployerJobSmartAnalysis = useEmployerJobTalent
